import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Alert,
  Button,
  Checkbox,
  Combobox,
  Container,
  Grid,
  Group,
  Input,
  InputBase,
  List,
  Stack,
  Table,
  Text,
  useCombobox,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { ErrorBoundary } from "@sentry/react";
import { ButtonLink, ErrorComponent } from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { WKT } from "ol/format";
import type { Polygon } from "ol/geom";
import "ol/ol.css";
import { getArea } from "ol/sphere";
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";

import { api } from "@/api.ts";
import Area from "@/components/Area";
import { ImportAreaSelectionDialog } from "@/components/ImportAreaSelectionDialog.tsx";
import { MapWrapper, useMapDrawSingle } from "@/components/MapWrapper.tsx";
import AuthorizationTermsAndConditions from "@/components/authorization/AuthorizationTermsAndConditions.tsx";
import { MAX_AREA, MAX_NODES } from "@/constants.ts";
import { useCatalogItems } from "@/hooks/useCatalogItems.ts";
import type { FormatItem } from "@/hooks/useFormatItems.ts";
import { useFormatItems } from "@/hooks/useFormatItems.ts";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";

export const Route = createFileRoute("/flows/download/selection/$identifier")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title:
          globalT`Sweco REALity` +
          " - " +
          globalT`Download` +
          " - " +
          globalT`Select data`,
      },
    ],
  }),
});

const FormatOption: FC<FormatItem> = ({ title, description }) => (
  <Stack gap="xs">
    <Text fz="sm" fw={500}>
      {title}
    </Text>
    <Text fz="xs" opacity={0.6}>
      {description}
    </Text>
  </Stack>
);
const FormatSelector: FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const formatItems = useFormatItems();

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={(val) => {
        onChange(val);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          pointer
          rightSection={<Combobox.Chevron />}
          onClick={() => combobox.toggleDropdown()}
          rightSectionPointerEvents="none"
          multiline
          style={{ minWidth: "250px" }}
        >
          {formatItems[value] ? (
            <span style={{ textWrap: "nowrap" }}>
              {formatItems[value].title}
            </span>
          ) : (
            <Input.Placeholder>
              <Trans>Choose format</Trans>
            </Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          {Object.entries(formatItems).map(([v, item]) => (
            <Combobox.Option key={v} value={v}>
              <FormatOption value={v} {...item!} />
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

function RouteComponent() {
  const { data: project } = useSuspenseQueryPostgrest(
    api
      .from("project")
      .select("id,identifier,name,project_dataset(*)")
      .eq("identifier", Route.useParams().identifier)
      .single(),
  );

  const catalog = useCatalogItems();

  const [selectedDatasets, setSelectedDatasets] = useState<Array<string>>(
    (Route.useSearch() as any).datasets ?? [],
  );

  const [format, setFormat] = useState("application/zip;profile=open-standard");

  const { setupMap, geometry, setGeometry } = useMapDrawSingle();

  const wkt = useMemo(
    () =>
      geometry &&
      `SRID=4326;${new WKT().writeGeometry(geometry, {
        dataProjection: "EPSG:4326",
        featureProjection: "EPSG:3857",
      })}`,
    [geometry],
  );
  const area = useMemo(() => (geometry ? getArea(geometry) : 0), [geometry]);
  const nodes = useMemo(
    () => (geometry ? (geometry as Polygon).getCoordinates().length : 0),
    [geometry],
  );

  const { t } = useLingui();
  const openImportFileDialog = useCallback(() => {
    modals.open({
      id: "import-area-selection-dialog",
      title: t`Import area from file`,
      children: (
        <ErrorBoundary
          fallback={({ error }) => <ErrorComponent error={error} />}
        >
          <ImportAreaSelectionDialog onSubmit={setGeometry} />
        </ErrorBoundary>
      ),
      size: "xl",
    });
  }, [setGeometry, t]);

  return (
    <Container size="lg">
      <Grid>
        <Grid.Col span={12}>
          <Stack>
            <p style={{ textAlign: "center", marginBottom: 0 }}>
              <Trans>You are downloading data on behalf of</Trans>
            </p>
            <strong style={{ textAlign: "center" }}>
              {project.identifier} {project.name}
            </strong>
          </Stack>
        </Grid.Col>
        <Grid.Col span={12}>
          <h3>
            <Trans>Instructions</Trans>
          </h3>
          <AuthorizationTermsAndConditions>
            <Text size="sm" fw={500} ff="heading">
              Användning av tjänsten
            </Text>
            <List size="sm" withPadding spacing="xs" mb={0} type="ordered">
              <List.Item>
                Gå igenom allmänna villkor med uppdragsledare för aktuellt
                uppdrag
              </List.Item>
              <List.Item>
                Välj datamängder, uttagsområde och format nedan
              </List.Item>
              <List.Item>
                Gå vidare för att läsa och godkänna villkoren
              </List.Item>
              <List.Item>
                När beställningen påbörjats kan du antingen vänta kvar på sidan
                tills nedladdningslänk visas, eller vänta tills du får en
                mailnotifikation
              </List.Item>
            </List>
          </AuthorizationTermsAndConditions>
        </Grid.Col>
        <Grid.Col span={4}>
          <h3>
            1. <Trans>Datasets</Trans>
          </h3>
          <Table withRowBorders={false}>
            <Table.Tbody>
              {catalog.data.features.map((dataset) => (
                <Table.Tr key={dataset.id}>
                  <Table.Td>
                    <Checkbox
                      disabled={
                        !dataset.links.some(
                          (l) =>
                            l.rel ===
                              "http://www.opengis.net/def/rel/ogc/1.0/execute" &&
                            l.href.endsWith("/processes/extraction/execution"),
                        )
                      }
                      checked={selectedDatasets.includes(
                        catalog.uriFor(dataset),
                      )}
                      onChange={(evt) =>
                        setSelectedDatasets((selected) => {
                          if (evt.target.checked) {
                            return [...selected, catalog.uriFor(dataset)];
                          } else {
                            return selected.filter(
                              (uri) => uri !== catalog.uriFor(dataset),
                            );
                          }
                        })
                      }
                    />
                  </Table.Td>
                  <Table.Td>{dataset.properties.title}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Grid.Col>
        <Grid.Col span={8}>
          <h3>
            2. <Trans context="geographical area">Area</Trans>
          </h3>
          <Stack>
            <MapWrapper width="100%" height="300px" setup={setupMap} />
            <Group justify="space-between">
              <Text c={area > MAX_AREA ? "alert" : undefined}>
                {area && area > 0.01 ? (
                  <span>
                    <Trans>Area</Trans>: <Area value={area} />
                  </span>
                ) : (
                  <Trans>Draw your area of interest on the map</Trans>
                )}
              </Text>
              {area > MAX_AREA ? (
                <Alert
                  variant="light"
                  color="alert"
                  title=""
                  icon={<FontAwesomeIcon icon={faCircleExclamation} />}
                >
                  <Trans>
                    The area is to large, the maximum is 20 000 ha (200 km²).
                  </Trans>
                </Alert>
              ) : nodes > MAX_NODES ? (
                <Alert
                  variant="light"
                  color="alert"
                  title=""
                  icon={<FontAwesomeIcon icon={faCircleExclamation} />}
                >
                  <Trans>
                    The number of nodes in the geometry is to high, the maximum
                    is {MAX_NODES}.
                  </Trans>
                </Alert>
              ) : null}
              <Button
                onClick={openImportFileDialog}
                variant="light"
                color="green"
              >
                <Trans>Import file</Trans>
              </Button>
            </Group>
          </Stack>
        </Grid.Col>
        <Grid.Col span={12}>
          <Group>
            <FormatSelector value={format} onChange={setFormat} />
            <ButtonLink
              style={{ flex: "1 1" }}
              disabled={
                selectedDatasets.length === 0 ||
                wkt === undefined ||
                area > MAX_AREA ||
                nodes > MAX_NODES
              }
              to="/flows/download/authorize/$identifier"
              params={{ identifier: project.identifier || "" }}
              search={{
                datasets: selectedDatasets,
                geometry: wkt!,
                format,
              }}
              color="green"
            >
              <Trans>Continue</Trans>
            </ButtonLink>
          </Group>
        </Grid.Col>
      </Grid>
    </Container>
  );
}
